import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ChevronLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/Button';
import SelectDropdown from '../components/SelectDropdown';

interface DetailedProfileForm {
  preferredName: string;
  phoneNumber: string;
  gender: string;
  age: string;
  educationStatus: string;
  school?: string;
  educationLevel?: string;
  gradeLevel?: string;
  program?: string;
  programCustom?: string;
  completedEducationLevel?: string;
  completedSchool?: string;
  completedYear?: string;
  completedProgram?: string;
  primarySkillArea: string;
  technicalSkills: string[];
}

const UserDetailedProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [newSkill, setNewSkill] = useState('');
  const [formData, setFormData] = useState<DetailedProfileForm>({
    preferredName: '',
    phoneNumber: '',
    gender: '',
    age: '',
    educationStatus: '',
    school: '',
    educationLevel: '',
    gradeLevel: '',
    program: '',
    programCustom: '',
    completedEducationLevel: '',
    completedSchool: '',
    completedYear: '',
    completedProgram: '',
    primarySkillArea: '',
    technicalSkills: []
  });

  // Initialize form with current user data
  useEffect(() => {
    if (user) {
      const profile = user.profile || {};
      const educationData = (profile as any).education || {};
      const skillsData = (profile as any).skills || {};

      setFormData({
        preferredName: profile.preferredName || '',
        phoneNumber: profile.phoneNumber || '',
        gender: (profile as any).gender || '',
        age: (profile as any).age ? String((profile as any).age) : '',
        educationStatus: educationData.status || '',
        school: educationData.school || '',
        educationLevel: educationData.level || '',
        gradeLevel: educationData.gradeLevel || '',
        program: educationData.program || '',
        programCustom: educationData.programCustom || '',
        completedEducationLevel: educationData.completedLevel || '',
        completedSchool: educationData.completedSchool || '',
        completedYear: educationData.completedYear || '',
        completedProgram: educationData.completedProgram || '',
        primarySkillArea: skillsData.primaryArea || '',
        technicalSkills: skillsData.technicalSkills || []
      });
    }
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddSkill = () => {
    if (newSkill.trim()) {
      setFormData(prev => ({
        ...prev,
        technicalSkills: [...prev.technicalSkills, newSkill.trim()]
      }));
      setNewSkill('');
    }
  };

  const handleRemoveSkill = (index: number) => {
    setFormData(prev => ({
      ...prev,
      technicalSkills: prev.technicalSkills.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    if (!formData.preferredName.trim()) {
      toast.error('Preferred name is required');
      return;
    }

    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        profile: {
          preferredName: formData.preferredName,
          phoneNumber: formData.phoneNumber,
          gender: formData.gender,
          age: formData.age ? parseInt(formData.age) : undefined,
          education: {
            status: formData.educationStatus,
            ...(formData.educationStatus === 'currently-studying' && {
              school: formData.school,
              level: formData.educationLevel,
              gradeLevel: formData.gradeLevel,
              ...(formData.educationLevel === 'college' && {
                program: formData.program === 'other' ? undefined : formData.program,
                programCustom: formData.program === 'other' ? formData.programCustom : undefined
              })
            }),
            ...(formData.educationStatus === 'completed-education' && {
              completedLevel: formData.completedEducationLevel,
              completedSchool: formData.completedSchool,
              completedYear: formData.completedYear,
              completedProgram: formData.completedProgram
            })
          },
          skills: {
            primaryArea: formData.primarySkillArea,
            technicalSkills: formData.technicalSkills
          }
        }
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/users/profile`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          credentials: 'include',
          body: JSON.stringify(payload)
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update profile');
      }

      // Update auth context
      if (data.success && data.data?.user) {
        const updatedUser = {
          id: data.data.user.id,
          email: data.data.user.email,
          profile: data.data.user.profile,
          isEmailVerified: data.data.user.isEmailVerified,
          role: data.data.user.role,
          lastLogin: data.data.user.lastLogin,
          lastActive: data.data.user.lastActive,
          createdAt: data.data.user.createdAt
        };
        updateUser(updatedUser);
      }

      toast.success('Profile updated successfully!');
      navigate('/panel/profile');
    } catch (error) {

      toast.error(error instanceof Error ? error.message : 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDiscard = () => {
    navigate('/panel/profile');
  };

  return (
    <div className="profile-details-page">
      <div className="profile-details-page__container">
        <div className="profile-details-page__header">
          <button
            className="profile-details-page__back-btn"
            onClick={handleDiscard}
            aria-label="Go back to profile"
          >
            <ChevronLeft size={24} />
          </button>
          <div className="profile-details-page__header-content">
            <h1 className="profile-details-page__title">Edit Profile</h1>
            <p className="profile-details-page__subtitle">Complete your profile to help us personalize your experience</p>
          </div>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="profile-details-page__form">
          {/* Basic Information Section */}
          <div className="profile-details-page__form-section">
            <div className="profile-details-page__form-section-header">
              <span className="material-symbols-outlined profile-details-page__section-icon">person</span>
              <h2 className="profile-details-page__form-section-title">Basic Information</h2>
            </div>

            <div className="profile-details-page__form-row">
              <div className="form-control">
                <label className="form-control__label">Preferred Name</label>
                <input
                  type="text"
                  name="preferredName"
                  className="form-input"
                  value={formData.preferredName}
                  onChange={handleInputChange}
                  placeholder="Enter your preferred name"
                />
              </div>

              <div className="form-control">
                <label className="form-control__label">Phone Number</label>
                <input
                  type="tel"
                  name="phoneNumber"
                  className="form-input"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  placeholder="+63 9000000000"
                />
              </div>
            </div>

            <div className="profile-details-page__form-row">
              <div className="form-control">
                <label className="form-control__label">Gender</label>
                <SelectDropdown
                  value={formData.gender}
                  onChange={(value) => setFormData({ ...formData, gender: value })}
                  options={[
                    { value: '', label: 'Select gender' },
                    { value: 'male', label: 'Male' },
                    { value: 'female', label: 'Female' },
                    { value: 'other', label: 'Other' },
                    { value: 'prefer-not-to-say', label: 'Prefer not to say' },
                  ]}
                  ariaLabel="Gender"
                />
              </div>

              <div className="form-control">
                <label className="form-control__label">Age</label>
                <input
                  type="number"
                  name="age"
                  className="form-input"
                  value={formData.age}
                  onChange={handleInputChange}
                  placeholder="25"
                  min="13"
                  max="150"
                />
              </div>
            </div>
          </div>

          <div className="profile-details-page__form-divider" />

          {/* Education Status Section */}
          <div className="profile-details-page__form-section">
            <div className="profile-details-page__form-section-header">
              <span className="material-symbols-outlined profile-details-page__section-icon">school</span>
              <h2 className="profile-details-page__form-section-title">Education Status</h2>
            </div>

            <div className="form-control">
              <label className="form-control__label">Current Status</label>
              <SelectDropdown
                value={formData.educationStatus}
                onChange={(value) => setFormData({ ...formData, educationStatus: value })}
                options={[
                  { value: '', label: 'Select status' },
                  { value: 'currently-studying', label: 'Currently Studying' },
                  { value: 'completed-education', label: 'Completed Education' },
                  { value: 'not-studying', label: 'Not Currently Studying' },
                  { value: 'prefer-not-to-say', label: 'Prefer Not to Say' },
                ]}
                ariaLabel="Education status"
              />
            </div>

            {formData.educationStatus === 'currently-studying' && (
              <>
                <div className="form-control">
                  <label className="form-control__label">School/University</label>
                  <input
                    type="text"
                    name="school"
                    className="form-input"
                    value={formData.school}
                    onChange={handleInputChange}
                    placeholder="Enter your school or university name"
                  />
                </div>

                <div className="profile-details-page__form-row">
                  <div className="form-control">
                    <label className="form-control__label">Education Level</label>
                    <SelectDropdown
                      value={formData.educationLevel || ''}
                      onChange={(value) => setFormData({ ...formData, educationLevel: value, gradeLevel: '' })}
                      options={[
                        { value: '', label: 'Select level' },
                        { value: 'grade-school', label: 'Grade School' },
                        { value: 'high-school', label: 'High School' },
                        { value: 'senior-high', label: 'Senior High School' },
                        { value: 'college', label: 'College / University' },
                      ]}
                      ariaLabel="Education level"
                    />
                  </div>

                  <div className="form-control">
                    <label className="form-control__label">
                      {formData.educationLevel === 'grade-school' && 'Grade Level (1-6)'}
                      {formData.educationLevel === 'high-school' && 'Grade Level (7-10)'}
                      {formData.educationLevel === 'senior-high' && 'Grade Level (11-12)'}
                      {formData.educationLevel === 'college' && 'Year Level'}
                      {!formData.educationLevel && 'Grade/Year Level'}
                    </label>
                    <SelectDropdown
                      value={formData.gradeLevel || ''}
                      onChange={(value) => setFormData({ ...formData, gradeLevel: value })}
                      options={
                        formData.educationLevel === 'grade-school'
                          ? [
                              { value: '', label: 'Select grade' },
                              { value: 'grade-1', label: 'Grade 1' },
                              { value: 'grade-2', label: 'Grade 2' },
                              { value: 'grade-3', label: 'Grade 3' },
                              { value: 'grade-4', label: 'Grade 4' },
                              { value: 'grade-5', label: 'Grade 5' },
                              { value: 'grade-6', label: 'Grade 6' },
                            ]
                          : formData.educationLevel === 'high-school'
                          ? [
                              { value: '', label: 'Select grade' },
                              { value: 'grade-7', label: 'Grade 7' },
                              { value: 'grade-8', label: 'Grade 8' },
                              { value: 'grade-9', label: 'Grade 9' },
                              { value: 'grade-10', label: 'Grade 10' },
                            ]
                          : formData.educationLevel === 'senior-high'
                          ? [
                              { value: '', label: 'Select grade' },
                              { value: 'grade-11', label: 'Grade 11' },
                              { value: 'grade-12', label: 'Grade 12' },
                            ]
                          : formData.educationLevel === 'college'
                          ? [
                              { value: '', label: 'Select year' },
                              { value: '1st-year', label: '1st Year' },
                              { value: '2nd-year', label: '2nd Year' },
                              { value: '3rd-year', label: '3rd Year' },
                              { value: '4th-year', label: '4th Year' },
                              { value: '5th-year', label: '5th Year+' },
                            ]
                          : [{ value: '', label: 'Select level first' }]
                      }
                      disabled={!formData.educationLevel}
                      ariaLabel="Grade or year level"
                    />
                  </div>
                </div>

                {formData.educationLevel === 'college' && (
                  <>
                    <div className="form-control">
                      <label className="form-control__label">Course/Program</label>
                      <SelectDropdown
                        value={formData.program || ''}
                        onChange={(value) => setFormData({ ...formData, program: value, programCustom: '' })}
                        options={[
                          { value: '', label: 'Select program' },
                          { value: 'computer-science', label: 'Computer Science / IT' },
                          { value: 'engineering', label: 'Engineering' },
                          { value: 'mechanical-engineering', label: 'Mechanical Engineering' },
                          { value: 'civil-engineering', label: 'Civil Engineering' },
                          { value: 'electrical-engineering', label: 'Electrical Engineering' },
                          { value: 'business-admin', label: 'Business Administration' },
                          { value: 'commerce', label: 'Commerce' },
                          { value: 'accountancy', label: 'Accountancy' },
                          { value: 'medicine', label: 'Medicine / Healthcare' },
                          { value: 'nursing', label: 'Nursing' },
                          { value: 'psychology', label: 'Psychology' },
                          { value: 'education', label: 'Education' },
                          { value: 'law', label: 'Law' },
                          { value: 'architecture', label: 'Architecture' },
                          { value: 'arts-design', label: 'Arts & Design' },
                          { value: 'tourism', label: 'Tourism & Hospitality' },
                          { value: 'agriculture', label: 'Agriculture' },
                          { value: 'marine-biology', label: 'Marine Biology' },
                          { value: 'other', label: 'Other' },
                        ]}
                        ariaLabel="Course or program"
                      />
                    </div>

                    {formData.program === 'other' && (
                      <div className="form-control">
                        <label className="form-control__label">Specify Your Course/Program</label>
                        <input
                          type="text"
                          className="form-input"
                          value={formData.programCustom || ''}
                          onChange={(e) => setFormData({ ...formData, programCustom: e.target.value })}
                          placeholder="Enter your course or program name"
                          maxLength={100}
                        />
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {formData.educationStatus === 'completed-education' && (
              <>
                <div className="form-control">
                  <label className="form-control__label">Highest Level Completed</label>
                  <SelectDropdown
                    value={formData.completedEducationLevel || ''}
                    onChange={(value) => setFormData({ ...formData, completedEducationLevel: value })}
                    options={[
                      { value: '', label: 'Select level' },
                      { value: 'grade-school', label: 'Grade School' },
                      { value: 'high-school', label: 'High School' },
                      { value: 'senior-high', label: 'Senior High School' },
                      { value: 'college', label: 'College / University' },
                    ]}
                    ariaLabel="Highest education level completed"
                  />
                </div>

                <div className="form-control">
                  <label className="form-control__label">School/University Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.completedSchool || ''}
                    onChange={(e) => setFormData({ ...formData, completedSchool: e.target.value })}
                    placeholder="Enter school or university name"
                    maxLength={100}
                  />
                </div>

                <div className="profile-details-page__form-row">
                  <div className="form-control">
                    <label className="form-control__label">Course/Program</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.completedProgram || ''}
                      onChange={(e) => setFormData({ ...formData, completedProgram: e.target.value })}
                      placeholder="e.g., Computer Science, Business Administration"
                      maxLength={100}
                    />
                  </div>

                  <div className="form-control">
                    <label className="form-control__label">Year Completed</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.completedYear || ''}
                      onChange={(e) => setFormData({ ...formData, completedYear: e.target.value })}
                      placeholder="e.g., 2020"
                      maxLength={4}
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="profile-details-page__form-divider" />

          {/* Skills & Interests Section */}
          <div className="profile-details-page__form-section">
            <div className="profile-details-page__form-section-header">
              <span className="material-symbols-outlined profile-details-page__section-icon">star</span>
              <h2 className="profile-details-page__form-section-title">Skills & Interests</h2>
            </div>

            <div className="form-control">
              <label className="form-control__label">Primary Skill Area</label>
              <SelectDropdown
                value={formData.primarySkillArea}
                onChange={(value) => setFormData({ ...formData, primarySkillArea: value })}
                options={[
                  { value: '', label: 'Select skill area' },
                  { value: 'software-development', label: 'Software Development' },
                  { value: 'data-science', label: 'Data Science' },
                  { value: 'ui-ux-design', label: 'UI/UX Design' },
                  { value: 'marketing', label: 'Marketing' },
                  { value: 'content-writing', label: 'Content Writing' },
                  { value: 'other', label: 'Other' },
                ]}
                ariaLabel="Primary skill area"
              />
            </div>

            <div className="form-control">
              <label className="form-control__label">Technical Skills</label>
              <div className="skills-input-container">
                <input
                  type="text"
                  className="form-input skills-input"
                  value={newSkill}
                  onChange={(e) => setNewSkill(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddSkill();
                    }
                  }}
                  placeholder="Type a skill and press Enter or click Add"
                />
                <button
                  type="button"
                  className="profile-details-add-skill-btn"
                  onClick={handleAddSkill}
                >
                  Add Skill
                </button>
              </div>

              {formData.technicalSkills.length > 0 && (
                <div className="skills-pills">
                  {formData.technicalSkills.map((skill, index) => (
                    <div key={index} className="skill-pill">
                      <span className="skill-pill__text">{skill}</span>
                      <button
                        type="button"
                        className="skill-pill__remove"
                        onClick={() => handleRemoveSkill(index)}
                        aria-label={`Remove ${skill}`}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="profile-details-page__actions">
            <Button
              variant="secondary"
              onClick={handleDiscard}
              disabled={isLoading}
            >
              Discard Changes
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : 'Save Profile'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserDetailedProfilePage;

